import { Minus, Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { trackCTA } from '../../utils/metaPixel';

const FAQ_ITEM_IDS = ['free', 'plans', 'cancel', 'renew', 'optimizations', 'refunds'] as const;

type FaqItemId = (typeof FAQ_ITEM_IDS)[number];

interface PricingFAQProps {
  onNavigate?: (view: string) => void;
}

/** FAQPage schema — keep in sync with views.pricing.faq.items in en.json */
export function buildPricingFaqJsonLd(items: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

export const pricingFaqJsonLd = buildPricingFaqJsonLd([
  {
    q: 'What is included in the free plan?',
    a: 'The free plan includes 3 lifetime resume tailorings with Fast AI, job discovery and ranking, basic ATS scoring, and up to 3 application tracker entries — no credit card required.',
  },
  {
    q: 'Which paid plan should I choose?',
    a: 'Monthly (₹349) suits active job hunts with 60 optimizations per month. Quarterly (₹749) and Half-Yearly (₹1,499) bundle more optimizations at up to 28% savings — ideal for planned or longer searches.',
  },
  {
    q: 'Can I cancel my subscription anytime?',
    a: 'Yes. Paid plans auto-renew but you can cancel anytime from your dashboard. You keep access until the end of your current billing period.',
  },
  {
    q: 'How does auto-renew work?',
    a: 'Monthly, quarterly, and half-yearly plans renew automatically at the end of each billing cycle so your optimizations never pause mid-search. You can turn off auto-renew before the next charge.',
  },
  {
    q: 'What counts as one optimization?',
    a: 'Each time you tailor your resume to a specific job description — including ATS scoring and keyword alignment — that uses one optimization credit from your plan allowance.',
  },
  {
    q: 'Are purchases refundable?',
    a: 'Credit and subscription purchases are generally final. We may issue refunds for billing errors or technical issues — see our Refund & Cancellation Policy for details.',
  },
]);

const PricingFAQ: React.FC<PricingFAQProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<FaqItemId | null>('free');

  const items = useMemo(
    () =>
      FAQ_ITEM_IDS.map((id) => ({
        id,
        q: t(`views.pricing.faq.items.${id}.question`),
        a: t(`views.pricing.faq.items.${id}.answer`),
      })),
    [t],
  );

  const toggle = (id: FaqItemId) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section id="pricing-faq" className="home-faq-section scroll-mt-28" aria-labelledby="pricing-faq-title">
      <div className="home-faq-section__shell">
        <div className="home-faq-layout">
          <header className="home-faq-intro">
            <p className="home-faq-intro__eyebrow">{t('views.pricing.faq.eyebrow')}</p>
            <h2 id="pricing-faq-title" className="home-faq-intro__title">
              {t('views.pricing.faq.title')}
            </h2>
            <p className="home-faq-intro__hint">{t('views.pricing.faq.hint')}</p>
            <button
              type="button"
              className="home-faq-intro__cta"
              onClick={() => {
                trackCTA('pricing_faq_get_started');
                onNavigate?.('signup');
              }}
            >
              {t('views.pricing.faq.cta')}
            </button>
          </header>

          <div className="home-faq-list">
            {items.map(({ id, q, a }) => {
              const isOpen = openId === id;
              return (
                <div key={id} className={`home-faq-item${isOpen ? ' home-faq-item--open' : ''}`}>
                  <button
                    type="button"
                    className="home-faq-item__trigger"
                    aria-expanded={isOpen}
                    onClick={() => toggle(id)}
                  >
                    <span className="home-faq-item__question">{q}</span>
                    <span
                      className={`home-faq-item__toggle${isOpen ? ' home-faq-item__toggle--open' : ''}`}
                      aria-hidden
                    >
                      {isOpen ? <Minus className="h-4 w-4" strokeWidth={2.5} /> : <Plus className="h-4 w-4" strokeWidth={2.5} />}
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="home-faq-item__answer">
                      <p>{a}</p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingFAQ;
